import { markRaw, type Component } from 'vue'
import {
  useModal,
  useSheet,
  type SheetEvents,
  type SheetHeight,
  type SheetStack,
} from '@dacr/library'

import { useDevice } from '@/shared/composables/useDevice'

export enum Dialog {
  NewCopyrightOwner,
  RenameFolder,
  MoveItems,
  AddFolder,
  Share,
  FileAccess,
  FileSettings,
  FilePrivacySettings,
  FileOwners,
  FileAuthors,
  UpdateFilePrivacy,
  CopyrightInfo,
  Registrations,
  ChangeName,
  ChangeEmail,
  ChangeUsername,
  ChangeTitle,
  ChangeBio,
  ChangePassword,
  DefaultFilePrivacy,
  CopyrightOwners,
  EditCopyrightOwner,
  DeactivateOrDelete,
  AccountClosureInfo,
  CancelPlan,
  SelectPlanBilling,
  PlanDetails,
  UpcomingBillingDetails,
  ProMembershipDetails,
  AddPaymentMethod,
  ResubscribePayment,
  CantPay,
  IAP,
  ResubscribeIAP,
  AllOwners,
  AllAuthors,
  FromInvite,
  InfringementReport,
  BuyReportCredits,
  PayReportCredits,
  CreateReport,
  SelectCopyright,
  FlaggedMatch,
  UploadDetails,
  ReportFeedback,
  UpdatedTerms,
  ShareReport,
  RegisterCountries,
  ConfirmRegisterCountries,
  DacrMembership,
}

interface DialogEntry {
  modal: Component
  sheet: Component
  height?: SheetHeight
}

interface DialogOpenOptions {
  props?: Record<string, unknown>
  events?: SheetEvents
  stack?: boolean | SheetStack
  height?: SheetHeight
  preventOutsideClick?: boolean
  hideClose?: boolean
  disableDrag?: boolean
}

const registry = new Map<Dialog, DialogEntry>()

export const registerDialog = (name: Dialog, entry: DialogEntry) => {
  registry.set(name, {
    modal: markRaw(entry.modal),
    sheet: markRaw(entry.sheet),
    height: entry.height,
  })
}

export const openDialog = (name: Dialog, opts: DialogOpenOptions = {}) => {
  const entry = registry.get(name)
  if (!entry) return
  const { isMobile } = useDevice()

  if (isMobile.value) {
    return useSheet().open(entry.sheet, {
      props: opts.props,
      events: opts.events,
      name: Dialog[name],
      stack: opts.stack,
      height: opts.height ?? entry.height,
      preventOutsideClick: opts.preventOutsideClick,
      disableDrag: opts.disableDrag,
    })
  }

  return useModal().open(entry.modal, {
    props: opts.props,
    events: opts.events,
    name: Dialog[name],
    stack: !!opts.stack,
    hideClose: opts.hideClose,
    preventOutsideClick: opts.preventOutsideClick,
  })
}

export const closeDialog = (data?: unknown) => {
  const { isMobile } = useDevice()
  return (isMobile.value ? useSheet() : useModal()).close(data)
}

export const closeAllDialogs = () => {
  const { isMobile } = useDevice()
  return (isMobile.value ? useSheet() : useModal()).closeAll()
}

export const triggerDialogEvent = (
  event: keyof SheetEvents,
  data?: unknown
) => {
  const { isMobile } = useDevice()
  return (isMobile.value ? useSheet() : useModal()).triggerEvent(event, data)
}
